import React from "react";
import { CommandPermissions, Mentionable } from "../../../../common/sesh_types";
import { SearchableDropdown } from "./searchable_dropdown";
import { cloneDeep } from "lodash";
import { Note } from "./improved_components/note";
import { Constants, SeshPermission } from "../../../../common/constants";
import { InfoTooltip } from "./improved_components/tooltips/info_tooltip";

interface PermissionsFormProp {
	initial_permissions: CommandPermissions[];
	role_mentions: Mentionable[];
	save_func: (new_permissions: CommandPermissions[]) => void;
}

interface PermissionsFormState {
	permissions: CommandPermissions[];
}

const command_to_description: Record<SeshPermission, string> = {
	create: "Create events (/create) and create time finder polls",
	poll: "Create polls (/poll)",
	ai: "Use sesh ai (/ai)",
	list: "List events (/list)",
	link: "Generate .ics feeds (/link)",
	delete: "Delete events (/delete)",
	remind: "Set personal reminders (/remind)",
	timestamp: "Generate Discord timestamps (/timestamp)",
	view_logs: "View logs",
	settings: "Manage sesh",
};

const info_descriptions: Partial<Record<SeshPermission, string | JSX.Element>> =
	{
		settings: (
			<>
				This permission allows users to modify sesh settings and
				edit/delete sesh events and polls, even if they did not create
				them.
				<br />
				<br />
				<span className="font-bold">
					This is a dangerous permission to grant.
				</span>
			</>
		),
	};

export class PermissionsForm extends React.Component<
	PermissionsFormProp,
	PermissionsFormState
> {
	constructor(props) {
		super(props);

		this.state = {
			permissions: cloneDeep(this.props.initial_permissions),
		};

		this.handleChange = this.handleChange.bind(this);
	}

	renderCommand(command_permissions: CommandPermissions) {
		let initial_mentions = command_permissions.roles_ids.map((val) =>
			this.props.role_mentions.find(
				(mentionable) => mentionable.id === val
			)
		);

		// make sure val exists
		let has_everyone = false;
		initial_mentions = initial_mentions.filter((val) => {
			if (!has_everyone) has_everyone = val?.id === "@everyone";
			return Boolean(val);
		});

		const should_display_warning_note =
			has_everyone && initial_mentions.length > 1;

		return (
			<div key={command_permissions.command} className="pb-4">
				<div className="flex gap-2 pb-1">
					<b>{command_to_description[command_permissions.command]}</b>
					{info_descriptions[command_permissions.command] && (
						<InfoTooltip
							info={
								info_descriptions[command_permissions.command]
							}
						></InfoTooltip>
					)}
				</div>
				<div>
					<SearchableDropdown
						options={this.props.role_mentions.filter(
							(val) => val.id !== "@here"
						)}
						initial_mentions={initial_mentions}
						handleChange={(event) =>
							this.handleChange(
								command_permissions.command,
								event
							)
						}
						placeholder={`Manage ${
							command_permissions.command === "settings"
								? "server"
								: "sesh"
						} users only`}
						ignore_invalid_permissions={true}
					></SearchableDropdown>
				</div>
				{should_display_warning_note && (
					<div>
						<p className="error-note mt-4">
							<span className="has-text-weight-bold">
								@everyone
							</span>{" "}
							will take precedence and all other roles will be
							ignored.
						</p>
					</div>
				)}
			</div>
		);
	}

	handleChange(command: string, mentionables: Mentionable[]) {
		let current_command = this.state.permissions.find(
			(val) => val.command === command
		);

		current_command.roles_ids = mentionables.map((val) => val.id);

		this.setState({ permissions: this.state.permissions });
	}

	renderAllCommands() {
		const all_elements = this.state.permissions.map((val) =>
			this.renderCommand(val)
		);

		return <div>{all_elements}</div>;
	}

	render() {
		return (
			<div>
				<h1 className="title is-3 has-text-centered">Permissions</h1>
				<Note>
					These permissions determine which roles can use which sesh
					features both in Discord and on the web interface. <br />
					<br /> Note that you can also limit use of slash commands
					via{" "}
					<a
						className="manual-link"
						href="https://support.discord.com/hc/en-us/articles/10952896421783"
					>
						Discord integration settings
					</a>
					, though modifiying integration permissions will not affect
					who can use the web interface.
				</Note>
				<div className="py-5">
					<div>{this.renderAllCommands()}</div>
				</div>
				<div className="field submit-button">
					<div className="control columns is-mobile is-vcentered">
						<div className="column is-narrow">
							<button
								className="button dashboard-primary-button is-size-4-tablet is-size-5-mobile"
								onClick={() => {
									this.props.save_func(
										this.state.permissions
									);
								}}
							>
								Save
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	}
}
